import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "../../App";
import { mono, Btn, Select, Input, Field, PageTitle, Drawer } from "./ptUi";

// Studio Calendar — week grid (studios × days). Renders the union of shoots
// (from pt_shoots) and manual bookings (pt_bookings). Clash = red outline.
// Mirrors the Excel "Studio Calendar" sheet.

const BOOKING_TYPES = ["Shoot", "Prep", "Maintenance", "External", "Hold"];
const STATUSES = ["Confirmed", "Tentative", "Cancelled"];
const addDays = (ds, n) => { const d = new Date(ds); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const dayLabel = (ds) => new Date(ds).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });

export default function PtrackerCalendar() {
  const { user } = useAuth();
  const isLead = ["admin", "srmanager", "manager", "creativedirector", "associatecreativedirector"].includes((user?.role || "").toLowerCase());
  const [week, setWeek] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);

  const load = () => api.get(`/ptracker/calendar?week=${week}`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [week]);

  const days = data?.week || [];
  const studios = data?.studios || [];
  const cellFor = (studio, day) => {
    const shoots = (data?.shoots || []).filter((s) => s.studio === studio && s.date === day);
    const bookings = (data?.bookings || []).filter((b) => b.studio === studio && b.date === day);
    return { shoots, bookings, clash: shoots.length + bookings.filter((b) => b.status === "Confirmed").length > 1 };
  };

  return (
    <div style={{ padding: "24px 28px", maxWidth: 1400, margin: "0 auto" }} data-testid="ptracker-calendar">
      <PageTitle title="Studio Calendar" sub="Who's in which studio this week — shoots + bookings. Click an empty cell to book."
        right={
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <span onClick={() => setWeek(addDays(days[0] || week, -7))} style={{ cursor: "pointer", color: "var(--t2)", padding: "6px 10px", border: "1px solid var(--bdr)", borderRadius: "var(--radius-sm)" }}>‹</span>
            <span style={{ fontFamily: mono, fontSize: 11, color: "var(--t1)" }}>{days[0] ? `${dayLabel(days[0])} – ${dayLabel(days[6])}` : ""}</span>
            <span onClick={() => setWeek(addDays(days[0] || week, 7))} style={{ cursor: "pointer", color: "var(--t2)", padding: "6px 10px", border: "1px solid var(--bdr)", borderRadius: "var(--radius-sm)" }}>›</span>
          </div>
        } />

      {studios.length === 0 ? (
        <div style={{ border: "1px dashed var(--bdr)", borderRadius: "var(--radius-sm)", padding: 40, textAlign: "center", fontFamily: mono, fontSize: 12, color: "var(--t3)" }}>No studios configured in Dream Machine.</div>
      ) : (
        <div style={{ overflowX: "auto", border: "1px solid var(--bdr)", borderRadius: "var(--radius-sm)", background: "var(--s1)" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 820 }}>
            <thead>
              <tr>
                <th style={{ width: 110, background: "var(--s2)", padding: "8px 10px", textAlign: "left", fontFamily: mono, fontSize: 10, color: "var(--t3)", borderRight: "1px solid var(--bdr)" }}>STUDIO</th>
                {days.map((day) => <th key={day} style={{ padding: "8px 4px", background: day === today ? "var(--rd)" : "var(--s2)", color: day === today ? "var(--r)" : "var(--t3)", fontFamily: mono, fontSize: 10 }}>{dayLabel(day)}</th>)}
              </tr>
            </thead>
            <tbody>
              {studios.map((studio) => (
                <tr key={studio}>
                  <td style={{ background: "var(--s2)", padding: "8px 10px", fontFamily: mono, fontSize: 11, color: "var(--t2)", borderRight: "1px solid var(--bdr)", borderTop: "1px solid var(--bdr)" }}>{studio}</td>
                  {days.map((day) => {
                    const { shoots, bookings, clash } = cellFor(studio, day);
                    return (
                      <td key={day} onClick={() => isLead && setDrawer({ date: day, studio })}
                        style={{ height: 64, minWidth: 100, borderTop: "1px solid var(--bdr)", borderLeft: "1px solid var(--bdr)", padding: 4, verticalAlign: "top", cursor: isLead ? "pointer" : "default", outline: clash ? "2px solid var(--r)" : "none", outlineOffset: -2 }}>
                        {shoots.map((s) => (
                          <div key={s.id} onClick={(e) => { e.stopPropagation(); navigate("/ptracker/shoots"); }}
                            style={{ marginBottom: 3, borderRadius: 4, padding: "2px 6px", background: "var(--r)", color: "#fff", fontFamily: mono, fontSize: 9, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>🎬 {s.label}</div>
                        ))}
                        {bookings.map((b) => (
                          <div key={b.id} onClick={(e) => { e.stopPropagation(); if (isLead) setDrawer(b); }}
                            style={{ marginBottom: 3, borderRadius: 4, padding: "2px 6px", fontFamily: mono, fontSize: 9, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                              background: b.status === "Confirmed" ? "rgba(16,163,12,.15)" : "rgba(217,119,6,.15)", color: b.status === "Confirmed" ? "#0ca30c" : "#d97706" }}>
                            {b.label || b.booking_type} · {b.booking_type}
                          </div>
                        ))}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ display: "flex", gap: 14, marginTop: 10, fontFamily: mono, fontSize: 10, color: "var(--t3)" }}>
        <span><span style={{ background: "var(--r)", color: "#fff", padding: "1px 5px", borderRadius: 3 }}>🎬</span> Shoot</span>
        <span><span style={{ background: "rgba(16,163,12,.15)", color: "#0ca30c", padding: "1px 5px", borderRadius: 3 }}>Booking</span> Confirmed</span>
        <span style={{ color: "var(--r)" }}>▢ Clash</span>
        {isLead && <span>Click an empty cell to book.</span>}
      </div>

      {drawer && <BookingForm booking={drawer} studios={studios} onClose={() => setDrawer(null)} onSaved={() => { setDrawer(null); load(); }} />}
    </div>
  );
}

function BookingForm({ booking, studios, onClose, onSaved }) {
  const [f, setF] = useState({ date: booking.date || "", studio: booking.studio || "", label: booking.label || "", booking_type: booking.booking_type || "Prep", status: booking.status || "Confirmed", notes: booking.notes || "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const editing = !!booking.id;
  const save = () => { if (!f.date || !f.studio) return alert("Date and studio are required."); setSaving(true); const req = editing ? api.put(`/ptracker/bookings/${booking.id}`, f) : api.post("/ptracker/bookings", f); req.then(onSaved).catch((e) => { alert(e.response?.data?.detail || "Save failed"); setSaving(false); }); };
  const del = () => { if (window.confirm("Delete this booking?")) api.delete(`/ptracker/bookings/${booking.id}`).then(onSaved).catch(() => {}); };
  return (
    <Drawer title={editing ? "Edit booking" : "New booking"} onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Date"><Input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} /></Field>
          <Field label="Studio"><Select options={studios} value={f.studio} onChange={(e) => set("studio", e.target.value)} allowEmpty /></Field>
          <Field label="Type"><Select options={BOOKING_TYPES} value={f.booking_type} onChange={(e) => set("booking_type", e.target.value)} /></Field>
          <Field label="Status"><Select options={STATUSES} value={f.status} onChange={(e) => set("status", e.target.value)} /></Field>
        </div>
        <Field label="Label (brand / project / who)"><Input value={f.label} onChange={(e) => set("label", e.target.value)} /></Field>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
          <div>{editing && <Btn kind="danger" onClick={del}>Delete</Btn>}</div>
          <div style={{ display: "flex", gap: 8 }}><Btn kind="ghost" onClick={onClose}>Cancel</Btn><Btn onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Btn></div>
        </div>
      </div>
    </Drawer>
  );
}
